import React from 'react';
import { PWA_VERSION } from '../pwa-version';
import { useUpdateFlow } from './UpdateFlowContext';
import { applyServiceWorkerUpdate } from '../utils/apply-service-worker-update';

const INSTALLED_VERSION_KEY = 'pwa_installed_version';

export type PWAUpdateStep = 'none' | 'available' | 'ready';

export interface PWAUpdateContextValue {
  /** @deprecated Usa `useUpdateFlow().activeOffer` o `serviceWorkerWaiting`. */
  updateStep: PWAUpdateStep;
  /** True se conviene bloccare sync offline (SW in attesa, offerta aperta, o flusso update). */
  hasPendingUpdate: boolean;
  updateRegistration: ServiceWorkerRegistration | null;
  /** Apre di nuovo il dialog di aggiornamento (es. dopo “Più tardi”). */
  applyUpdate: () => void;
  /** Ricarica la pagina (solo casi limite). */
  restartApp: () => void;
  isApplying: boolean;
}

const PWAUpdateContext = React.createContext<PWAUpdateContextValue>({
  updateStep: 'none',
  hasPendingUpdate: false,
  updateRegistration: null,
  applyUpdate: () => {},
  restartApp: () => {},
  isApplying: false,
});

/**
 * Compat layer sopra {@link useUpdateFlow}: niente più flusso SW a due passi in UI.
 */
export function PWAUpdateProvider({ children }: { children: React.ReactNode }) {
  const flow = useUpdateFlow();

  const requestSwOffer = React.useCallback(() => {
    const installed = localStorage.getItem(INSTALLED_VERSION_KEY);

    // Stessa versione: apply silenzioso invece di mostrare il dialog
    if (installed === PWA_VERSION) {
      void navigator.serviceWorker.getRegistration().then((reg) => {
        if (reg?.waiting) void applyServiceWorkerUpdate(reg);
      });
      return;
    }

    flow.requestUpdateOffer({
      reason: 'service-worker',
      fromVersion: installed,
      toVersion: PWA_VERSION,
    });
  }, [flow]);

  const restartApp = React.useCallback(() => {
    try {
      window.location.reload();
    } catch {
      window.location.replace(window.location.pathname + window.location.search);
    }
  }, []);

  const value = React.useMemo<PWAUpdateContextValue>(() => {
    const compactEntry =
      flow.serviceWorkerWaiting &&
      !flow.activeOffer &&
      !flow.isUpdateInProgress;

    return {
      updateStep: compactEntry ? 'available' : 'none',
      hasPendingUpdate: flow.shouldBlockOfflineSync,
      updateRegistration: null,
      applyUpdate: requestSwOffer,
      restartApp,
      isApplying: false,
    };
  }, [
    flow.serviceWorkerWaiting,
    flow.activeOffer,
    flow.isUpdateInProgress,
    flow.shouldBlockOfflineSync,
    requestSwOffer,
    restartApp,
  ]);

  return <PWAUpdateContext.Provider value={value}>{children}</PWAUpdateContext.Provider>;
}

export function usePWAUpdate(): PWAUpdateContextValue {
  return React.useContext(PWAUpdateContext);
}
